import { useT } from '@transifex/react'
import { FulfilmentDetail } from '../../../../../../models/fulfilment'
import { useAppSelector } from '../../../../../../state/hooks'
import { selectSaleFulfilmentDetail } from '../../../../../../state/slices/currentSale/currentSaleSlice'
import {
  AddressErrorsStatus,
  AddressErrorStatusTypes,
  useFulfillmentHelper,
} from '../hooks/useFulfillmentHelper'

export type IncorrectAddressMessageType =
  | 'address'
  | 'billingAddress'
  | 'shippingAddress'

type LineItemFulfilmentFixSaleAddressesModel = {
  fulfilmentDetail: FulfilmentDetail | undefined
  incorrectAddressMessage?: string
  incorrectAddressDescription?: string
  shouldShowFulfilmentAddress: boolean
}

const getIncorrectAddressMessageType = (
  status: AddressErrorStatusTypes
): IncorrectAddressMessageType | undefined => {
  if (status === AddressErrorsStatus.INCORRECTSHIPPING_INCORRECTBILLING) {
    return 'address'
  }

  if (status === AddressErrorsStatus.CORRECTSHIPPING_INCORRECTBILLING) {
    return 'billingAddress'
  }

  if (status === AddressErrorsStatus.INCORRECTSHIPPING_CORRECTBILLING) {
    return 'shippingAddress'
  }

  return undefined
}

const getIncorrectAddressMessage = (
  type: IncorrectAddressMessageType | undefined,
  t: ReturnType<typeof useT>
) => {
  if (type === 'address') {
    return t('Existing address is incorrect', {
      _context: 'Popover content',
    })
  }

  if (type === 'billingAddress') {
    return t('Existing billing address is incorrect', {
      _context: 'Popover content',
    })
  }

  if (type === 'shippingAddress') {
    return t('Existing shipping address is incorrect', {
      _context: 'Popover content',
    })
  }

  return undefined
}

export const useLineItemFulfilmentFixSaleAddressesModel = (): LineItemFulfilmentFixSaleAddressesModel => {
  const t = useT()
  const fulfilmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const { getFulfillmentAddressStatus } = useFulfillmentHelper()

  const status = getFulfillmentAddressStatus(fulfilmentDetail)
  const incorrectAddressMessageType = getIncorrectAddressMessageType(status)
  const incorrectAddressMessage = getIncorrectAddressMessage(
    incorrectAddressMessageType,
    t
  )

  return {
    fulfilmentDetail,
    incorrectAddressMessage,
    incorrectAddressDescription: incorrectAddressMessage
      ? t('Update the address to use it for this sale.', {
          _context: 'Popover description',
        })
      : undefined,
    shouldShowFulfilmentAddress:
      status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
      status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING,
  }
}
